import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (attemptsRef.current >= 8) {
        if (!cancelled) setState("error");
        return;
      }
      attemptsRef.current += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        console.error(e);
        if (!cancelled) setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const tweet = `I just helped send Camden Clichee (Wookies #10) to the NATTYS 2026 in Mesa, AZ 🏀 Join me ${shareUrl}`;

  return (
    <div data-testid="success-page" className="min-h-screen bg-[#09090B] text-white font-body flex items-center justify-center px-6 py-24">
      <div className="max-w-2xl w-full">
        {state === "checking" && (
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#00E5FF]" />
            <h1 className="font-display text-5xl md:text-6xl uppercase tracking-tight mt-8">
              Confirming your gift…
            </h1>
            <p className="text-[#A1A1AA] mt-4">Hold tight — we're checking with Stripe.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-success-state" className="text-center">
            <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30">
              <CheckCircle2 className="h-10 w-10 text-[#00E5FF]" />
            </div>
            <div className="mt-6 text-xs uppercase tracking-[0.3em] text-[#A1A1AA]">
              Donation received
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-[0.9]">
              You're sending Camden<br />to the <span className="text-[#FACC15]">NATTYS.</span>
            </h1>
            {info && (
              <p className="text-lg text-[#FAFAFA] mt-6">
                <span className="text-[#00E5FF] tabular font-semibold">
                  ${((info.amount_total || 0) / 100).toFixed(2)}
                </span>{" "}
                from <span className="font-semibold">{info.donor_name || "a friend"}</span>.
              </p>
            )}
            {info?.message && (
              <p className="italic text-[#A1A1AA] mt-4 max-w-lg mx-auto">"{info.message}"</p>
            )}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/"
                data-testid="back-home-button"
                className="inline-flex items-center gap-2 bg-[#FF007F] text-white font-bold uppercase tracking-widest px-7 py-4 hover:-translate-y-0.5 transition-transform shadow-pink-glow"
              >
                <ArrowLeft className="h-4 w-4" /> Back to Camden's page
              </Link>
              <button
                data-testid="share-after-donation-button"
                onClick={() => {
                  navigator.clipboard.writeText(tweet);
                  toast.success("Link copied — share with your crew!");
                }}
                className="inline-flex items-center gap-2 border border-[#27272A] text-white px-7 py-4 uppercase tracking-widest font-bold hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>
          </div>
        )}

        {(state === "expired" || state === "error") && (
          <div className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-[#FF007F]" />
            <h1 className="font-display text-5xl uppercase tracking-tight mt-6">
              Something went sideways
            </h1>
            <p className="text-[#A1A1AA] mt-4">
              We couldn't confirm your payment. If your card was charged it will still
              appear on the donor wall once Stripe finalizes it.
            </p>
            <Link
              to="/"
              data-testid="back-home-button-error"
              className="inline-flex items-center gap-2 bg-[#FF007F] text-white font-bold uppercase tracking-widest px-7 py-4 mt-10"
            >
              <ArrowLeft className="h-4 w-4" /> Back home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
